import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const IntegrationFramework = () => {
  const steps = [
    {
      title: "Partnership Onboarding",
      desc: "QNC and partner teams align on strategic objectives, operational models, and integration scope.",
      details: [
        "Compliance and custodial alignment are reviewed collaboratively.",
        "Smart contract infrastructure is defined and linked through Enzyme’s audited architecture.",
        "Integration scope and execution timeline are agreed before launch.",
      ],
    },
    {
      title: "Vault Integration",
      desc: "Partner strategies or infrastructure components are deployed within QNC’s modular vault architecture.",
      details: [
        "Strategies are encoded as Enzyme-compliant smart modules.",
        "Performance and risk controls are configured directly on-chain.",
        "Custody connections and permissions are managed via secure, verified connectors.",
      ],
    },
    {
      title: "Live Execution & Co-Market Launch",
      desc: "Once deployed, vaults operate autonomously — with ongoing collaboration across performance, transparency, and outreach.",
      details: [
        "Performance metrics remain visible through Enzyme dashboards.",
        "Partners gain allocator and investor exposure through QNC’s distribution channels.",
        "Joint case studies and visibility campaigns showcase live execution and results.",
      ],
    },
  ];

  const [open, setOpen] = useState(null);

  return (
    <section
      id="frameworks"
      className="relative py-24 md:py-28 bg-gradient-to-b from-[#050B1A] via-[#081B3F] to-[#0A2A66] text-white overflow-hidden"
    >
      <div className="max-w-6xl mx-auto px-6">
        <motion.h2
          className="text-3xl md:text-5xl font-bold text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          Integration Framework
        </motion.h2>

        <div className="relative">
          <div className="hidden md:block absolute left-1/2 top-0 -ml-0.5 w-1 h-full bg-gradient-to-b from-[#4DB2D3] via-[#3D8FAA] to-[#A5F5E2] rounded-full"></div>

          <div className="space-y-12 md:space-y-20">
            {steps.map((s, i) => {
              const isOpen = open === i;
              return (
                <motion.div
                  key={i}
                  className={`relative flex flex-col md:flex-row items-center ${
                    i % 2 === 0 ? "md:flex-row-reverse" : ""
                  }`}
                  initial={{ opacity: 0, x: i % 2 === 0 ? 60 : -60 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: i * 0.1 }}
                >
                  {/* Marker */}
                  <div className="hidden md:flex absolute left-1/2 -ml-6 items-center justify-center w-12 h-12 rounded-full bg-gradient-to-r from-[#4DB2D3] to-[#3D8FAA] text-white font-semibold shadow-lg ring-4 ring-[#0A2A66] z-10">
                    {i + 1}
                  </div>

                  {/* Card */}
                  <div
                    className={`group relative bg-white/5 border border-white/10 rounded-2xl shadow-lg hover:border-[#4DB2D3]/40 backdrop-blur-md transition-all w-full md:w-5/12 p-6 md:p-8 overflow-hidden ${
                      i % 2 === 0 ? "md:mr-auto md:pr-12" : "md:ml-auto md:pl-12"
                    }`}
                  >
                    {/* Mobile marker */}
                    <div className="flex md:hidden items-center mb-3">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-[#4DB2D3] to-[#3D8FAA] text-white font-semibold shadow-md mr-3">
                        {i + 1}
                      </div>
                      <h3 className="text-lg font-semibold text-[#A5F5E2]">{s.title}</h3>
                    </div>

                    {/* Title */}
                    <h3 className="hidden md:block text-xl font-semibold text-[#A5F5E2] mb-3">
                      {s.title}
                    </h3>

                    <p className="text-white/80 leading-relaxed">{s.desc}</p>

                    <button
                      onClick={() => setOpen(isOpen ? null : i)}
                      className="mt-4 text-sm font-medium text-[#4DB2D3] hover:text-[#A5F5E2] transition-colors"
                    >
                      {isOpen ? "Hide details ▲" : "View details ▼"}
                    </button>

                    <AnimatePresence>
                      {isOpen && (
                        <motion.ul
                          className="mt-4 pl-4 space-y-2 text-white/70 text-sm list-disc"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.4 }}
                        >
                          {s.details.map((d, x) => (
                            <li key={x}>{d}</li>
                          ))}
                        </motion.ul>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default IntegrationFramework;
